/**
 * Cached borrower-submission reads — Applications, Credit Authorizations, and
 * Borrower-Portal Users.
 *
 * All three follow the exact same fetch-once-then-cache contract as
 * useCachedLoanPipeline: the list is fetched a single time, written to the
 * cachedData reducer, and reused on every later mount. The next time a screen
 * mounts it reads straight from cache — it only hits the network when the cache
 * is empty. This is what kills the infinite refetch loop the screens had before
 * (a non-memoized hook fn in a useEffect dep array re-firing every render): here
 * `ensure()` depends only on `cached`, and an `inFlight` ref guards re-entry, so
 * re-creating the fetcher each render is harmless.
 *
 * Live endpoints (borrower_submissions / borrower_portal_admin routers). No
 * sample/fixture fallback — an empty or failed read just yields an empty list.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { cachedDataItemSet } from "../redux/actions/cachedDataActions";
import { CACHED_DATA_REDUCER_TYPES } from "../redux/reducers/cachedDataReducer";
import { useBorrowerSubmissionsHook } from "./useBorrowerSubmissionsHook";
import { useBorrowerPortalHook } from "./useBorrowerPortalHook";

/**
 * Generic cached-list hook. `fetcher` is a useBorrowerSubmissionsHook-style
 * function taking { onSuccessFn, onFailFn, onCompleteFn }.
 *
 * - `auto: true` fetches on mount when not cached.
 * - returns { data, isLoaded, loading, ensure, reload }.
 */
const useCachedResource = ({ key, fetcher, auto = false }) => {
  const dispatch = useDispatch();
  const cached = useSelector((s) => s.cachedData?.[key]);
  const inFlight = useRef(false);
  const [loading, setLoading] = useState(false);

  // Imperative fetch — not memoized (so it always sees the latest `fetcher`);
  // callers invoke it through the guarded `ensure` / `reload` below.
  const doFetch = () => {
    inFlight.current = true;
    setLoading(true);
    fetcher({
      onSuccessFn: (d) =>
        dispatch(cachedDataItemSet(key, Array.isArray(d) ? d : [])),
      onFailFn: () => dispatch(cachedDataItemSet(key, [])),
      onCompleteFn: () => {
        inFlight.current = false;
        setLoading(false);
      },
    });
  };

  // Fetch only if we don't already have it cached and nothing is in flight.
  const ensure = useCallback(() => {
    if (Array.isArray(cached) || inFlight.current) return;
    doFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cached]);

  // Force a re-fetch (Refresh button) regardless of cache.
  const reload = () => {
    if (inFlight.current) return;
    doFetch();
  };

  useEffect(() => {
    if (auto) ensure();
  }, [auto, ensure]);

  return {
    data: Array.isArray(cached) ? cached : [],
    isLoaded: Array.isArray(cached),
    loading,
    ensure,
    reload,
  };
};

export const useCachedLoanApplications = ({ auto = false } = {}) => {
  const { getApplications } = useBorrowerSubmissionsHook();
  return useCachedResource({
    key: CACHED_DATA_REDUCER_TYPES.LOAN_APPLICATIONS,
    fetcher: getApplications,
    auto,
  });
};

export const useCachedCreditAuthorizations = ({ auto = false } = {}) => {
  const { getCreditAuthorizations } = useBorrowerSubmissionsHook();
  return useCachedResource({
    key: CACHED_DATA_REDUCER_TYPES.CREDIT_AUTHORIZATIONS,
    fetcher: getCreditAuthorizations,
    auto,
  });
};

export const useCachedBorrowerPortalUsers = ({ auto = false } = {}) => {
  const { getBorrowerPortalUsers } = useBorrowerPortalHook();
  return useCachedResource({
    key: CACHED_DATA_REDUCER_TYPES.BORROWER_PORTAL_USERS,
    fetcher: getBorrowerPortalUsers,
    auto,
  });
};
